import React from "react";
import { nanoid } from "nanoid";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { CheckinSettingsResponse } from "@/types/booking-checkin-settings";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface AdditionalInfoTabProps {
  settings: CheckinSettingsResponse | null;
  setSettings: React.Dispatch<
    React.SetStateAction<CheckinSettingsResponse | null>
  >;
}

type OptionalFieldKey = "enabledHomeAddress" | "enabledIBAN";
const MAX_CUSTOM_ADDITIONAL_FIELDS = 20;

interface OptionalFieldDefinition {
  key: OptionalFieldKey;
  labelKey: string;
  labelDefaultValue: string;
  placeholderKey: string;
  placeholderDefaultValue: string;
  valueKey: "homeAddress" | "iban";
}

const OPTIONAL_FIELDS: OptionalFieldDefinition[] = [
  {
    key: "enabledHomeAddress",
    labelKey: "common.checkinSettings.additionalInfo.fields.homeAddress",
    labelDefaultValue: "Home address",
    placeholderKey:
      "common.checkinSettings.additionalInfo.placeholders.homeAddress",
    placeholderDefaultValue: "Address",
    valueKey: "homeAddress",
  },
  {
    key: "enabledIBAN",
    labelKey: "common.checkinSettings.additionalInfo.fields.iban",
    labelDefaultValue: "IBAN",
    placeholderKey: "common.checkinSettings.additionalInfo.placeholders.iban",
    placeholderDefaultValue: "FR00 12345678901234567890123",
    valueKey: "iban",
  },
];

export const AdditionalInfoTab: React.FC<AdditionalInfoTabProps> = ({
  settings,
  setSettings,
}) => {
  const { t } = useTranslation();
  const [customFieldName, setCustomFieldName] = React.useState("");
  const handleSettingChange = (
    key: keyof CheckinSettingsResponse,
    value: CheckinSettingsResponse[keyof CheckinSettingsResponse],
  ) => {
    setSettings((previousSettings) =>
      previousSettings
        ? {
            ...previousSettings,
            [key]: value,
          }
        : previousSettings,
    );
  };

  if (!settings) {
    return null;
  }

  const availableOptionalFields = OPTIONAL_FIELDS.filter(
    (field) => !settings[field.key],
  );
  const customAdditionalFields = settings.customAdditionalFields ?? [];
  const trimmedCustomFieldName = customFieldName.trim();
  const isCustomFieldLimitReached =
    customAdditionalFields.length >= MAX_CUSTOM_ADDITIONAL_FIELDS;
  const hasDuplicateCustomFieldName = customAdditionalFields.some(
    (field) =>
      field.label.trim().toLowerCase() === trimmedCustomFieldName.toLowerCase(),
  );
  const canAddCustomField =
    Boolean(trimmedCustomFieldName) &&
    !isCustomFieldLimitReached &&
    !hasDuplicateCustomFieldName;
  let customFieldHelperText = t(
    "common.checkinSettings.additionalInfo.customField.helper",
    {
      count: customAdditionalFields.length,
      max: MAX_CUSTOM_ADDITIONAL_FIELDS,
    },
  );

  if (isCustomFieldLimitReached) {
    customFieldHelperText = t(
      "common.checkinSettings.additionalInfo.customField.limitReached",
      {
        max: MAX_CUSTOM_ADDITIONAL_FIELDS,
      },
    );
  } else if (hasDuplicateCustomFieldName) {
    customFieldHelperText = t(
      "common.checkinSettings.additionalInfo.customField.duplicate",
    );
  }

  const handleAddOptionalField = (fieldKey: OptionalFieldKey) => {
    setSettings((previousSettings) =>
      previousSettings
        ? {
            ...previousSettings,
            enabledAdditionalInformation: true,
            [fieldKey]: true,
          }
        : previousSettings,
    );
  };

  const handleDeleteOptionalField = (field: OptionalFieldDefinition) => {
    setSettings((previousSettings) =>
      previousSettings
        ? {
            ...previousSettings,
            [field.key]: false,
            [field.valueKey]: "",
          }
        : previousSettings,
    );
  };

  const handleAddCustomField = () => {
    if (!canAddCustomField) {
      return;
    }

    setSettings((previousSettings) =>
      previousSettings
        ? {
            ...previousSettings,
            enabledAdditionalInformation: true,
            customAdditionalFields: [
              ...(previousSettings.customAdditionalFields ?? []),
              {
                id: nanoid(),
                label: trimmedCustomFieldName,
                order: previousSettings.customAdditionalFields?.length ?? 0,
                placeholder: "",
              },
            ],
          }
        : previousSettings,
    );
    setCustomFieldName("");
  };

  const handleCustomFieldNameKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    event.stopPropagation();

    if (event.key !== "Enter") return;

    event.preventDefault();
    handleAddCustomField();
  };

  const handleCustomFieldPlaceholderChange = (
    fieldId: string,
    placeholder: string,
  ) => {
    setSettings((previousSettings) =>
      previousSettings
        ? {
            ...previousSettings,
            customAdditionalFields: (
              previousSettings.customAdditionalFields ?? []
            ).map((field) =>
              field.id === fieldId ? { ...field, placeholder } : field,
            ),
          }
        : previousSettings,
    );
  };

  const handleDeleteCustomField = (fieldId: string) => {
    setSettings((previousSettings) =>
      previousSettings
        ? {
            ...previousSettings,
            customAdditionalFields: (
              previousSettings.customAdditionalFields ?? []
            )
              .filter((field) => field.id !== fieldId)
              .map((field, order) => ({ ...field, order })),
          }
        : previousSettings,
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Switch
            checked={settings.enabledAdditionalInformation}
            onCheckedChange={(checked) =>
              handleSettingChange("enabledAdditionalInformation", checked)
            }
          />
          <span className="text-lg font-medium">Additional info</span>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="new_outline" size="sm" className="gap-2">
              <Plus className="h-4 w-4" />
              {t("common.checkinSettings.additionalInfo.addOptionalField", {
                defaultValue: "Optional field",
              })}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            <DropdownMenuLabel className="text-muted-foreground text-xs font-medium">
              {t("common.checkinSettings.additionalInfo.predefinedFieldsTitle")}
            </DropdownMenuLabel>
            <DropdownMenuGroup>
              {availableOptionalFields.length > 0 ? (
                availableOptionalFields.map((field) => (
                  <DropdownMenuItem
                    key={field.key}
                    onClick={() => handleAddOptionalField(field.key)}
                  >
                    {t(field.labelKey, {
                      defaultValue: field.labelDefaultValue,
                    })}
                  </DropdownMenuItem>
                ))
              ) : (
                <DropdownMenuItem disabled>
                  {t(
                    "common.checkinSettings.additionalInfo.noPredefinedFields",
                  )}
                </DropdownMenuItem>
              )}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <div className="flex flex-col gap-2 p-2">
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground text-xs font-medium">
                  {t("common.checkinSettings.additionalInfo.customField.title")}
                </span>
                <span className="text-muted-foreground text-xs">
                  {customAdditionalFields.length}/{MAX_CUSTOM_ADDITIONAL_FIELDS}
                </span>
              </div>
              <div className="flex gap-2">
                <Input
                  value={customFieldName}
                  onChange={(event) => setCustomFieldName(event.target.value)}
                  onKeyDown={handleCustomFieldNameKeyDown}
                  placeholder={t(
                    "common.checkinSettings.additionalInfo.customField.namePlaceholder",
                  )}
                  maxLength={80}
                  disabled={isCustomFieldLimitReached}
                  aria-invalid={hasDuplicateCustomFieldName}
                  autoFocus
                />
                <Button
                  type="button"
                  size="icon_sm"
                  variant="new_outline"
                  disabled={!canAddCustomField}
                  onClick={handleAddCustomField}
                  aria-label={t(
                    "common.checkinSettings.additionalInfo.customField.add",
                  )}
                >
                  <Plus />
                </Button>
              </div>
              <span
                className={cn(
                  "text-xs",
                  hasDuplicateCustomFieldName
                    ? "text-destructive"
                    : "text-muted-foreground",
                )}
              >
                {customFieldHelperText}
              </span>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {settings.enabledAdditionalInformation ? (
        <div className="space-y-6 pl-6">
          {OPTIONAL_FIELDS.map((field) => (
            <div key={field.key} className="space-y-2">
              <div className="flex items-center gap-2">
                <Switch
                  checked={settings[field.key]}
                  onCheckedChange={(checked) =>
                    handleSettingChange(field.key, checked)
                  }
                />
                <span className="text-lg font-normal">
                  {t(field.labelKey, {
                    defaultValue: field.labelDefaultValue,
                  })}
                </span>
              </div>
              {settings[field.key] ? (
                <div className="flex items-center gap-2 pl-8">
                  <span className="text-muted-foreground">:</span>
                  <div className="relative flex-1">
                    <Input
                      value={settings[field.valueKey] || ""}
                      onChange={(event) =>
                        handleSettingChange(field.valueKey, event.target.value)
                      }
                      placeholder={t(field.placeholderKey, {
                        defaultValue: field.placeholderDefaultValue,
                      })}
                    />
                  </div>
                  <Button
                    variant="new_ghost"
                    size="icon_sm"
                    className="text-muted-foreground"
                    onClick={() => handleDeleteOptionalField(field)}
                    aria-label={t(
                      "common.checkinSettings.additionalInfo.removeOptionalField",
                      {
                        field: t(field.labelKey, {
                          defaultValue: field.labelDefaultValue,
                        }),
                      },
                    )}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ) : null}
            </div>
          ))}
          {customAdditionalFields.map((field) => (
            <div key={field.id} className="flex flex-col gap-2">
              <span className="text-lg font-normal">{field.label}</span>
              <div className="flex items-center gap-2 pl-8">
                <span className="text-muted-foreground">:</span>
                <Input
                  value={field.placeholder ?? ""}
                  onChange={(event) =>
                    handleCustomFieldPlaceholderChange(
                      field.id,
                      event.target.value,
                    )
                  }
                  placeholder={t(
                    "common.checkinSettings.additionalInfo.customField.valuePlaceholder",
                  )}
                  maxLength={120}
                />
                <Button
                  type="button"
                  variant="new_ghost"
                  size="icon_sm"
                  className="text-muted-foreground"
                  onClick={() => handleDeleteCustomField(field.id)}
                  aria-label={t(
                    "common.checkinSettings.additionalInfo.customField.remove",
                    { field: field.label },
                  )}
                >
                  <Trash2 />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="flex items-center gap-2">
        <Switch
          checked={settings.skipAdditionalInformation}
          onCheckedChange={(checked) =>
            handleSettingChange("skipAdditionalInformation", checked)
          }
        />
        <span className="text-lg font-normal">Skip button</span>
      </div>
    </div>
  );
};
